import { useEffect } from 'react';
import {
  setOpenItem,
  type OpenItemKind
} from '../lib/openItemStore';

/**
 * Record the item an editor route currently has open into the cross-route
 * {@link import('../lib/openItemStore').openItemStore open-item store}.
 *
 * Call it from a Document / Work Item editor page with the route's `kind` and
 * `:id`. It fires when the route mounts and again whenever the id changes
 * (i.e. the user switched to another item) — that is the "opened / viewed"
 * signal the standalone Agent panel reports as active context.
 *
 * This hook only ever SETS — it does not clear on unmount. Clearing is owned
 * centrally by the signed-in shell (`useClearStaleOpenItem`), which drops the
 * open item whenever the route is not an item editor or the active org changes,
 * so the agent's active target reflects what is CURRENTLY open rather than the
 * last item viewed. A falsy `id` (route param not yet resolved) is ignored.
 *
 * `title` is the open item's display title, carried so the Agent panel's
 * session hand-off can NAME the open item to the backend (the authoritative
 * `get_active_context` tool + the gateway-free prompt line). It is OPTIONAL and
 * may resolve a moment after the id (the editor loads the item async): the hook
 * re-runs when the title becomes known and updates the store, so a brief
 * id-only window is acceptable. A blank title is treated as unresolved.
 */
export function useTrackOpenItem(
  kind: OpenItemKind,
  id: string | undefined,
  title?: string
): void {
  useEffect(() => {
    if (!id) return;
    const trimmed = title?.trim();
    setOpenItem(
      trimmed ? { kind, id, title: trimmed } : { kind, id }
    );
  }, [kind, id, title]);
}
